import { Observable, forkJoin, Subject, of, throwError, BehaviorSubject, merge, EMPTY, defer, from, timer, combineLatest, firstValueFrom } from 'rxjs';
import { map, switchMap, catchError, ignoreElements, reduce, tap, filter, share, startWith } from 'rxjs/operators';
import _ from 'lodash';

import { CancelledError, IllegalStateError, chainError, findInAllChainedErrors, ResponseAPIError } from '@core/errors/errors-core';

import _LegacyServiceManager from '@core/services/legacy-service-manager';
import { createLogger } from '@core/services/logger.service';
import { EntityContext } from '@core/services/orm/orm';
import { makeProgressHandler, ProgressObserver } from '@core/tasks/progress';
import { GeneratorType, task, call } from '@core/tasks/generator';
import { callAsGenerator, callAsAsync, callAsAsyncFromTask, voidAbortSignal, voidProgress } from '@core/tasks/asyncCaller';
import { TaskQueue, QueuedTask, DefaultTaskContext } from '@core/tasks/queue';
import { IllegalArgumentError } from '@core/errors/errors-core';
import { ErrorHandlerService } from '@core/services/errorHandler';
import { FileService } from '@core/services/file/file';

import { PatchesDescriptor, PatchAttachmentDescriptor } from '@archipad/services/file/patchFile';
import workflowUserManager, { UserDataJson } from '@archipad/backend/project/workflowUserManager';
import bugNotePhase from '@archipad/services/bugNote.facade';
import * as Patch from '@archipad/backend/project/patch';
import _PatchHandlerService from '@archipad/backend/project/patch';
import { ProjectContext } from '@archipad/backend/project/projectContext';
import { CloudExtendedProjectInfo, CloudProjectContext, CloudProjectInfo, LocalProjectInfo, MinimalProjectInfo } from '@archipad/backend/project/projectInfo';
import { getCurrentProject, ProjectContextDataManager, BaseProjectContext } from '@archipad/backend/project/baseProjectContext';
import * as dependenciesManager from '@archipad/backend/project/dependenciesManager';
import {UpdateRemoteProjectInfo} from '@archipad/backend/archiweb/synchroBackend';
import { RemoteListProjectInfo, RemoteProjectInfo } from '@archipad/backend/archiweb/synchroBackend';
import * as synchroBackend from '@archipad/backend/archiweb/synchroBackend';
import * as synchroDriver from '@archipad/backend/archiweb/synchroDriver';
import * as sharedProjects from '@archipad/backend/archiweb/sharedProjects';
import * as projectTemplateManager from '@archipad/backend/project/projectTemplateManager';
import { isBugWorkflowEnabled } from '@archipad/backend/project/workflowManagerHelper';
import "@archipad/backend/archiweb/cloudFileResolver";
import "@archipad/services/file/file";
import { ProjectNotFound, WriteEmptyPatchSetError, ReadEmptyPatchSetError, ProjectError, MissingTempAttachmentsError } from '@archipad/errors/errors-archipad';
import { AttachmentSerializer, comparePatchId, MutablePatch, MutablePatchHeader, NetPatch, NetPatchAttachmentManifest, NetPatchHeader, PatchData, PatchHandlerContext, PatchNature, PatchService, PatchUserInfo } from "@archipad-js/core/patch";


import { ProjectEntity } from '@archipad-js/archipad-model';

import * as FormTemplate from '@ui-archipad/drivers/formTemplate';
import { findConfigBundleByName } from '@core/services/config';
import { getAccountService } from '@archipad/services/account.service';
import OfflineServiceInstance, { OfflineService } from "@archipad/services/offline.service";
import { getProjectInteraction } from './interactions';
import { ProjectSynchroState } from '@archipad/backend/project/projectManager';
import { ProjectRepositoryIndexedDB } from '@ui-archipad/backend/project/localStore';
import { LOADING_PROJECT_ATTACHMENTS_EVENT_TYPE, PRELOAD_PROJECT_ATTACHMENTS_EVENT_TYPE, PreloadProjectAttachmentsEvent } from '@archipad/services/preCacheProject.service';
import { Archipad_Auth_Service_DIID } from '@archipad-js/archipad/archiweb-services-client';
import { AccountInfo } from '@archipad/services/account.service.types';
import { isFeatureFlagsPlusAuthenticationServerEnable } from '@archipad/helpers/debug';
import { syncLog } from "@archipad/logs/logger";
import type { SyncReason } from "@core/services/logging/logAttributes";
import syncSessionService from "@archipad/logs/sync-session.service";
import { describeTempPatch, findMissingTempAttachments, keepTempPatch, listTempAttachments, removeAttachmentsFromManifest, TempAttachmentReference, TEMP_PATCH_VERSION, validateTempPatch } from '@archipad/backend/archiweb/tempPatch';

const log = createLogger('project');

/*---------------------------------------------------------------------------*/

// @TOOL prevent usage of dump patch mechanism
const USE_DUMP_PATCH_MECHANISM = true;


/*---------------------------------------------------------------------------*/

enum UpdateReason {
    Unknown = 0,
    Read,
    Write,
}



/*---------------------------------------------------------------------------*/

export function sanitizeRemoteProjectInfo(remoteProjectInfo: RemoteListProjectInfo | RemoteProjectInfo) : CloudProjectInfo {
    const value : CloudProjectInfo = {
        id: remoteProjectInfo.id,
        name: remoteProjectInfo.name,
        lastPatchId: remoteProjectInfo.lastPatchId,
        created: remoteProjectInfo.creationDate,
        lastModified: remoteProjectInfo.lastModificationDate,
        isOwner: remoteProjectInfo.owned,
        hasWorkflow: !!remoteProjectInfo.bugWorkflow,
        dependencies: remoteProjectInfo.dependencies as string[],
        isSampleProject: remoteProjectInfo.isSampleProject,
    };
    if ('photoUrl' in remoteProjectInfo) {
        value.photoUrl = remoteProjectInfo.photoUrl;
    }
    return value;
}


/*---------------------------------------------------------------------------*/


export interface ProjectManager {

    //--------------------------------------------------------------------------
    // Project Info

    getId() : string;

    getInfo(): Readonly<CloudExtendedProjectInfo>;

    refreshProjectInfo(progress: ProgressObserver): Observable<void>;

    notification$: Observable<unknown>;


    //--------------------------------------------------------------------------
    // Lifecycle

    getContext(progress: ProgressObserver): Observable<CloudProjectContext>;
    
    closeContext(progress: ProgressObserver): Observable<void>;

    hasContext(): boolean;


    //--------------------------------------------------------------------------
    // Project synchronization operations with S3

    publishChanges(progress: ProgressObserver): Observable<unknown>;

    refreshSynchroState(progress: ProgressObserver): Observable<ProjectSynchroState>;

    remove(progress:ProgressObserver): Observable<void>;

    rebase(progress: ProgressObserver): Observable<unknown>;

    saveProjectAsTemplate(progress: ProgressObserver, name:string):Observable<string>;


    //--------------------------------------------------------------------------
    // Project synchronization operations with temp patch

    tempPatchIsEmptyLike(): Observable<boolean>;

    fetchLatestTempChanges(progress: ProgressObserver): Observable<ProjectSynchroState>;

    discardTempLocalChanges(progress: ProgressObserver): Observable<ProjectSynchroState>;

    

    //--------------------------------------------------------------------------
    // Project synchronization operations with API server

    synchronizeUsers(progress: ProgressObserver): Observable<void>;

    // update existing project with remote info
    update(progress:ProgressObserver, remoteInfo: CloudProjectInfo, lastPatchId:string|null): Observable<boolean>;

    // Update local project info to tag it as offline mode
    changeOfflineMode(progress: ProgressObserver, offlineMode: boolean ): Promise<void>;


    //--------------------------------------------------------------------------
    // Debug and Commands

    logTempChanges(): Observable<void>;

    clear(progress: ProgressObserver): Observable<void>;

    upgradeProjectUsingContext(progress: ProgressObserver, context: ProjectContext): Observable<boolean>;

}


/*---------------------------------------------------------------------------*/

/**
 * project manager task queue (instance is shared because only one project can be opened at a time because the config service is shared)                                             
 */
 const taskQueue = new TaskQueue();

 interface TaskContext extends DefaultTaskContext {
     projectId: string;
 }
 
 function addTaskPolicy(queue:QueuedTask[], args:{ key:string, context:TaskContext, taskFn:(progress:ProgressObserver) => Observable<unknown> }): QueuedTask {
     if(!args.context){
         throw new IllegalArgumentError('Task context is missing')
     }
 
     if(args.key === 'closeContext') {
         // closeContext cancels every other task, except blocking tasks
         const tasks = queue.slice();
         for(const task of tasks) {
             if(task.key === 'closeContext'){
                 throw new IllegalStateError('Project context is already closing !')
             }
             if(task.context.blocking){
                 continue;
             }
 
             task.cancel();
         }
     } else {
         const task = queue.find((task) => (task.key === args.key));
         if(task){
             return task;    // if a task already exist, share it
         }
     }
 
     // append new task at end
     const task = new QueuedTask(args.key, args.taskFn, args.context);
     queue.push(task);
     return task;
 }
 
 taskQueue.addTaskPolicy = addTaskPolicy;
 
 /*---------------------------------------------------------------------------*/
 
 /**
  * Keeps the local project info in step with a patch saved by a template (creation or upgrade). It updates the
  * existing info rather than replacing it: an upgrade runs on each context opening, and a partial info (no
  * `registered`, `lastPatchId`...) would be reloaded as is on the next refresh - notably with no dependencies,
  * which the next publish would then push to the server.
  */
 const onSavePatch : projectTemplateManager.OnSavePatch = async (signal: AbortSignal, progress:ProgressObserver, userInfo:PatchUserInfo, projectInfo : UpdateRemoteProjectInfo) => {
     const p = makeProgressHandler(progress);
     p.total(1);
 	const projectRepositoryIndexedDB = new ProjectRepositoryIndexedDB();
     await callAsAsync(signal, p, 1, async () => {
         const existing = (await projectRepositoryIndexedDB.getProject(null, userInfo.id, projectInfo.id))?.info;
         const dependencies = projectInfo.dependencies?.length ? [ ...projectInfo.dependencies ] : (existing?.dependencies ?? []);
         const projectInfoForLocalStore : LocalProjectInfo = {
             ...existing,
             id: projectInfo.id,
             name: projectInfo.name ?? existing?.name,
             dependencies,
             hasWorkflow: dependencies.indexOf('workflow') !== -1,
             isOwner: existing?.isOwner ?? true,
             created: existing?.created ?? new Date().getTime(),  // not really true but not really important
         };
	     await callAsAsync(signal, p, 1, (po) => projectRepositoryIndexedDB.setProjectInfo(userInfo.id, projectInfo.id, projectInfoForLocalStore));
     });
 };
 

 export async function createProjectFromTemplate(signal: AbortSignal, progress:ProgressObserver, userInfo: PatchUserInfo,
    projectName:string, templateIds:string[], serverSideInstanciation:boolean): Promise<[ MinimalProjectInfo, ProjectManager ]> 
{
    const projectRepositoryIndexedDB = new ProjectRepositoryIndexedDB();
    const p = makeProgressHandler(progress);
    p.total(100);

    const projectInfo : MinimalProjectInfo = await callAsAsync(signal, p, 40, (po) => 
        projectTemplateManager.createFromTemplate(progress, projectName, templateIds, serverSideInstanciation, onSavePatch));

    const localProjectInfo = await callAsAsyncFromTask(signal, p, 5, (task) => 
        projectRepositoryIndexedDB.getProject(task, userInfo.id, projectInfo.id));

    const projectManager = MakeProjectManager(userInfo, localProjectInfo.info);

    const unsanitizedRemoteProjectInfo = await callAsAsync(signal, p, 10, (po) => 
        synchroDriver.getProjectInfo(po, projectInfo.id));
    const remoteProjectInfo = sanitizeRemoteProjectInfo(unsanitizedRemoteProjectInfo);

    // mark project as registered
    await callAsAsync(signal, p, 5, (po) => 
        projectManager.update(po, remoteProjectInfo, remoteProjectInfo.lastPatchId));

    await callAsAsync(signal, p, 10, (po) =>
        projectManager.synchronizeUsers(po));

    await callAsAsync(signal, p, 30, (po) =>
        projectManager.publishChanges(po));

    return [ projectInfo , projectManager ];
 }
 
 
 
//------------------------------------------------------------------------------
// Cloud project context
//------------------------------------------------------------------------------

interface CloudProjectContextDataManager extends ProjectContextDataManager {
    addBlockingTask<T = unknown>(progress: ProgressObserver, key: string, taskFn: (progress: ProgressObserver) => Observable<T>): Observable<T>;
}


class CloudProjectContextImpl extends BaseProjectContext {
    private _projectContextDataManager: CloudProjectContextDataManager;

    protected get _dataManager(): CloudProjectContextDataManager {
        return this._projectContextDataManager;
    }


    public constructor(projectContextDataManager: CloudProjectContextDataManager) {
        super();
        this._projectContextDataManager = projectContextDataManager;
    }

    public addBlockingTask<T = unknown>(progress: ProgressObserver, key: string, taskFn: (progress: ProgressObserver) => Observable<T>): Observable<T> {
        if(!this._dataManager){
            console.warn('Project has been closed before blocking task added');
            return of(null);
        }

        return this._dataManager.addBlockingTask<T>(progress, key, taskFn);
    }

}



const CONST_WAIT_FOR_GET_CONTEXT_BETWEEN_ATTEMPTS = 100;
const CONST_MAX_ATTEMPTS_GET_CONTEXT = 20;
/*---------------------------------------------------------------------------*/

class ProjectManagerImpl implements ProjectManager, CloudProjectContextDataManager {

    private _projectContext$: BehaviorSubject<CloudProjectContext>;

    private _notification$: Observable<unknown>;
    private _notificationSubject$: Subject<void>;

    private _userInfo: PatchUserInfo;
    private _projectInfo: LocalProjectInfo;

    // Synchro state related
    private _latestTempPatchStatus = null;
    private _hasUnflushedChanges = false;
    private _hasUnpublishedChanges = false;

    private _isDirty = false;

    private _attachmentSerializer:Patch.TempPatchAttachmentSerializer;

    private preLoadProjectAttachments$: Observable<unknown>;

    private triggerPreLoadProjectAttachments: Subject<void> = new Subject<void>();

    protected cacheRepository: ProjectRepositoryIndexedDB = new ProjectRepositoryIndexedDB();
    constructor(theUserInfo: PatchUserInfo, theProjectInfo: CloudProjectInfo | LocalProjectInfo,
        protected readonly patchService: PatchService = new PatchService(),
        protected readonly offlineService: OfflineService = new OfflineService(),
    ){
        const projectContext$ = new BehaviorSubject(null);
        const notificationSubject$ = new Subject<void>();
        const ready$ = taskQueue.ready$.pipe(
            tap(ready => ready === true ? this._notificationSubject$.next() : null),
        );

        const projectContextProcesses$ = projectContext$.pipe(
            switchMap((projectContext) => {
                if(!projectContext){
                    return EMPTY;
                }

                return projectContext.processes$
            }),
            catchError((err) => {
                ErrorHandlerService.handleError(err);
                return throwError(err);
            }),
            ignoreElements(),
        );

        // NOTE: `share()` and not `shareReplay()`: a late subscriber must not receive a stale value
        // instead of a new preload request. Explicit requests go through `_requestPreloadProjectAttachments()`.
        this.preLoadProjectAttachments$ = combineLatest([getAccountService().currentAccount$, this.triggerPreLoadProjectAttachments.pipe(startWith(void 0))]).pipe(
                switchMap(([account]) => this._requestPreloadProjectAttachments(account)),
                filter((requested) => requested),
                share(),
        );

        const notification$ = merge(
            ready$,
            notificationSubject$,
            // subscribe to the projectContext processes for as long as the
            // project notifications are observed
            projectContextProcesses$,
            this.preLoadProjectAttachments$,
        );


        this._projectContext$ = projectContext$;

        this._notificationSubject$ = notificationSubject$;
        this._notification$ = notification$;
        
        this._userInfo = theUserInfo;

        this._projectInfo = {
            id: null,
            name: null,
            isOwner: false,
            created: null,
            lastModified: null,
            photoUrl: null,
            reports: null,
            lastPatchId: null,
    
            // default values
            registered: false,
            hasWorkflow: false,
            dependencies: [],
            // not copied from the project info parameter
            approximateChangesCount: null,
            hasUnfetchedChanges: false,
    
            // cache
            aspects: [],
        };

        const mandatoryFields : Array<keyof LocalProjectInfo> = [
            'id',
            'name',
            'isOwner',
            'created',
        ];
        mandatoryFields.forEach((k:string) => {
            if (theProjectInfo[k] === undefined) {
                throw new ProjectError("Project instance cannot be created without " + k);
            }
    
            this._projectInfo[k] = theProjectInfo[k];
        });
    
        const otherFields : Array<keyof LocalProjectInfo> = [
            'lastPatchId',
            'lastModified',
            'photoUrl',
            'reports',
            'registered',
            'hasWorkflow',
            'dependencies',
            'aspects',
            'offlineFlag',
        ];
        // TODO:check if we need to pull aspects out from cache since it's recomputed by loadAspects
        otherFields.forEach((k:string) => {
            if (theProjectInfo[k] !== undefined) {
                this._projectInfo[k] = theProjectInfo[k];
            }
        });

        this._attachmentSerializer = new Patch.TempPatchAttachmentSerializer(this.getId())



    }

    public getId() { return this._projectInfo.id; }

    public get notification$(): Observable<unknown> { return this._notification$; }

    public async changeOfflineMode(progress: ProgressObserver, offlineMode: boolean): Promise<void> {
        const signal = voidAbortSignal();
        const p = makeProgressHandler(progress);
        p.total(1);
        this._projectInfo.offlineFlag = offlineMode;
        await callAsAsync(signal, p, 1, (po) => this.cacheRepository.setProjectInfo(this._userInfo.id, this._projectInfo.id, this._projectInfo));
        if (offlineMode) {
            await this._requestPreloadProjectAttachments();
        }
    }

    /**
     * Asks the service worker to preload the project attachments (offline projects only).
     * Duplicate requests are ignored by the service worker while a preload is running.
     *
     * @returns whether the request has been sent to the service worker
     */
    private async _requestPreloadProjectAttachments(account?: AccountInfo | null): Promise<boolean> {
        if (!this._projectInfo?.offlineFlag) {
            return false;
        }
        const projectId = this._projectInfo.id;
        navigator?.serviceWorker?.dispatchEvent(
            new MessageEvent(LOADING_PROJECT_ATTACHMENTS_EVENT_TYPE, {
                data: {
                    projectId,
                    loadedAttachmentsCount: 0,
                    totalAttachmentsCount: 0,
                    status: 'initializing',
                },
            }),
        );

        const oldIdentification = !isFeatureFlagsPlusAuthenticationServerEnable();
        if (oldIdentification) {
            await this.changeOfflineMode(voidProgress(), false);
            throw new IllegalStateError('Old identification system is not supported for offline projects');
        }
        if (OfflineServiceInstance.isOffline()) {
            return false;
        }

        // `controller` is null when the page is not controlled yet (first load, hard reload)
        const registration = await navigator?.serviceWorker?.getRegistration();
        const serviceWorker = navigator?.serviceWorker?.controller ?? registration?.active;
        if (!serviceWorker) {
            log.warn(`No active service worker, unable to preload attachments of project ${projectId}`);
            return false;
        }

        const currentAccount = account === undefined ? await firstValueFrom(getAccountService().currentAccount$) : account;
        const archipadAuthService = _LegacyServiceManager.container.get(Archipad_Auth_Service_DIID.symbol);
        const accessToken = await archipadAuthService.getAccessToken(voidAbortSignal());
        serviceWorker.postMessage({
            type: PRELOAD_PROJECT_ATTACHMENTS_EVENT_TYPE,
            data: {
                projectId,
                userInfo: {
                    id: currentAccount?.id,
                    email: currentAccount?.credential.login,
                    company_id: currentAccount?.company.id,
                },
                accessToken,
            },
        } satisfies PreloadProjectAttachmentsEvent);
        return true;
    }

    /** returns a readonly object */
    private _getProjectInfo(): Readonly<CloudExtendedProjectInfo> {
        // nested inner class
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const parent = this;
        class ProjectInfoProxy implements CloudExtendedProjectInfo {
            get id() { return parent._projectInfo.id; }
            get name() { return parent._projectInfo.name; }
            get isOwner() { return parent._projectInfo.isOwner; }
            get created() { return parent._projectInfo.created; }
            get lastModified() { return parent._projectInfo.lastModified; }
            get hasWorkflow() { return parent._projectInfo.hasWorkflow; }
            get dependencies() { return parent._projectInfo.dependencies; }
            
            get lastPatchId() { return parent._projectInfo.lastPatchId; }
            get photoUrl() { return parent._projectInfo.photoUrl; }
            get isSampleProject() { return parent._projectInfo.isSampleProject; }
            // @deprecated alias
            get photo() { return this.photoUrl; }

            get registered() { return parent._projectInfo.registered; }
            get reports() { return parent._projectInfo.reports; }
            get aspects() { return parent._projectInfo.aspects; }
            get approximateChangesCount() { return parent._projectInfo.approximateChangesCount; }
            get state() { return parent._getState(); }
            get isBusy() { return taskQueue.isRunning(); }
            get offlineFlag() { return parent._projectInfo.offlineFlag; }
            set offlineFlag(value) { console.error('offlineFlag is read-only', parent._projectInfo.offlineFlag, value); }
        }
        return new ProjectInfoProxy();
    }

    private get projectContext(): CloudProjectContext { return this._projectContext$.getValue(); }
    private set projectContext(projectContext: CloudProjectContext) { this._projectContext$.next(projectContext); }

    private _setTempPatchStatus(status) {
        this._latestTempPatchStatus = status;
        this._isDirty = false;
        this._notificationSubject$.next();
    }
    
    // TODO: remove reference and synchroTime from state
    private  _setStatusSynced() {
        this._latestTempPatchStatus = {
            state: synchroDriver.TempPatchState.IN_SYNC,
            rebase: false,
        };
        this._isDirty = false;

        this._notificationSubject$.next();
    }

    private _contextModified() {
        this._isDirty = true;

        if (this._latestTempPatchStatus &&
            (this._latestTempPatchStatus.state == synchroDriver.TempPatchState.CONFLICT ||
                this._latestTempPatchStatus.state == synchroDriver.TempPatchState.PUSH)) {
            // already in conflict or push state, nothing to do
        } else if (this._latestTempPatchStatus && this._latestTempPatchStatus.state == synchroDriver.TempPatchState.FETCH && this._hasUnflushedChanges) {
            // transform to conflict
            this._latestTempPatchStatus = {
                state: synchroDriver.TempPatchState.CONFLICT,
                rebase: false,
            };
        } else if (this._hasUnflushedChanges) {
            this._latestTempPatchStatus = {
                state: synchroDriver.TempPatchState.PUSH,
                rebase: false,
            };
        } else {
            this._latestTempPatchStatus = {
                state: synchroDriver.TempPatchState.IN_SYNC,
                rebase: false,
            };
        }

        this._notificationSubject$.next();
    }

    private _getState(): ProjectSynchroState {
        return {
            hasUnflushedChanges: this._hasUnflushedChanges,
            hasUnpublishedChanges: this._hasUnpublishedChanges,
            hasUnfetchedChanges: this._projectInfo.hasUnfetchedChanges,
            tempPatchStatus: this._latestTempPatchStatus,

            isDirty: () => this._isDirty,
        };
    }

    private *_refresh(progress: ProgressObserver): GeneratorType<ProjectSynchroState> {
        const p = makeProgressHandler(progress);
        p.total(2);
        if (this.offlineService.isOffline()) {
            return this._getState();
        }

        yield* this._updateHasUnfetchedChanges(p.task(1));
        
        return this._getState();
    }

    private  _updateTempPatchInfo(tempPatch:synchroDriver.TempPatch, updateReason:UpdateReason) {
        let unpublishedChanges;
        let unflushedChanges;
        
        if (tempPatch) {
            if (tempPatch.patch) {
                const header : NetPatchHeader = tempPatch.patch.patch[0];
                if (header.tempData && ! header.tempData.emptyLike) {
                    unpublishedChanges = true;
                    unflushedChanges = tempPatch.hasUnflushedChanges;
                }
                else {
                    unpublishedChanges = false;
                    unflushedChanges = tempPatch.hasUnflushedChanges;
                }    
            }
            else {
                unpublishedChanges = false;
                unflushedChanges = tempPatch.hasUnflushedChanges;
            }
        }
        else {
            unpublishedChanges = false;
            unflushedChanges = (updateReason == UpdateReason.Write);
        }

        const changed = unpublishedChanges != this._hasUnpublishedChanges || unflushedChanges != this._hasUnflushedChanges;

        this._hasUnpublishedChanges = unpublishedChanges;
        this._hasUnflushedChanges = unflushedChanges;

        // // TODO testing purpose
        // console.log(">>>" , "updateReason",updateReason,  "hasUnpublishedChanges",hasUnpublishedChanges, "hasUnflushedChanges",hasUnflushedChanges);

        if (changed) {
            this._notificationSubject$.next();
        }
    }

    private _updateApproxChangesCount(tempPatch) {
        const changes = tempPatch ? tempPatch.patch : null;
    
        if (!changes) {
            this._projectInfo.approximateChangesCount = null;
            return;
        }
    
        let count = 0;
    
        const patch = changes.patch;
    
        // TODO: check performances !!
        const idMatcher = /[0-9]{19}/;
        for (let i = 1; i < patch.length; i++) {
            const entityChanges = patch[i];
            for (let j = 1; j < entityChanges.length; j++) {
                const change = entityChanges[j];
                for (const k in change) {
                    if (!("string" == typeof change[k] && idMatcher.test(change[k]))) {
                        count++;
                    }
                }
            }
        }
    
        this._projectInfo.approximateChangesCount = count;
    }

    private *_getAvailablePatches() : GeneratorType<ReadonlySet<string>> {
        const p = makeProgressHandler(null);
        p.total(1);

        const _projectPatches = yield* call(p.oldTask(1, null, (task) => this.cacheRepository.listProjectPatches(task, this._userInfo.id, this._projectInfo.id)));
        const projectPatches = new Set(_projectPatches);

        projectPatches.delete(synchroDriver.CURRENT_PATCH);

        return projectPatches;
    }
    
    private *_hasAllPatches(requiredPatches: string[]) : GeneratorType<boolean> {
        const projectPatches = yield* this._getAvailablePatches();
    
        for (let i = 0; i < requiredPatches.length; i++) {
            if( !projectPatches.has(requiredPatches[i]) ){
                return false;
            }
        }
    
        return true;
    }

    private *_getPatch(progress: ProgressObserver, id: string) : GeneratorType<{patch: NetPatch, nature: PatchNature}> {
        const p = makeProgressHandler(progress);
        p.total(1);

        return yield* call(p.oldTask(1, null, () => this.cacheRepository.getPatchData(this._userInfo.id, this._projectInfo.id, id)));
    }

    private _makePatchHeader(entityContext:EntityContext, clientName:string, temp:boolean) : MutablePatchHeader {
        const header : MutablePatchHeader = {
            modelMinVersion: entityContext.modelMinVersion,
            modelVersion: entityContext.modelVersion,
            clientName: clientName,
            user: this._userInfo,
            temp: temp,
            emptyLike: false,
        };
        return header;
    }

    /**
     * @returns The temp attachments of the manifest whose file is not in the temp patch folder on S3
     */
    private *_findMissingTempAttachments(progress: ProgressObserver, manifest: NetPatchAttachmentManifest): GeneratorType<TempAttachmentReference[]> {
        const tempAttachments = listTempAttachments(manifest, this._attachmentSerializer);
        if (!tempAttachments.length) {
            return [];
        }

        const tempPatchFolder = this._attachmentSerializer.directory;
        const remoteAttachments = yield * call<PatchAttachmentDescriptor[]>(FileService.list(progress, tempPatchFolder.patches).pipe(
            reduce((attachments: PatchAttachmentDescriptor[], attachment: PatchAttachmentDescriptor) => {
                attachments.push(attachment);
                
                return attachments;
            }, []),
        ));
        const listedAttachmentPaths = new Set(remoteAttachments.map((attachment) => attachment.attachmentPath));

        const missingAttachments = findMissingTempAttachments(tempAttachments, listedAttachmentPaths);
        for (const attachment of missingAttachments) {
            this._syncLog.withMetadata({
                'archipad.entity.id': attachment.entityId,
                'archipad.attachment.key': attachment.attachmentKey,
                'archipad.attachment.url': attachment.url,
                'archipad.sync.temp_attachments_listed': remoteAttachments.length,
            }).error('sync.publish.missing_temp_attachment');
        }
        return missingAttachments;
    }

    /**
     * Makes sure every temp attachment of the patch is in the temp patch folder on S3 before publishing it.
     *
     * A missing file cannot be published, and publishing the patch without it loses it for good (the local changes are
     * cleared once published). So by default the publish fails with `MissingTempAttachmentsError` and the local changes
     * are kept untouched; the attachments are only removed from the patch when the user agreed to (`approvedMissingUrls`).
     *
     * @param approvedMissingUrls - `temp:` urls the user agreed to publish without
     * @throws MissingTempAttachmentsError if a missing attachment was not approved
     */
    private *_ensureTempAttachmentsArePublishable(progress: ProgressObserver, netPatch: NetPatch, approvedMissingUrls: ReadonlySet<string>): GeneratorType<void> {
        const missingAttachments = yield* this._findMissingTempAttachments(progress, netPatch.attachmentManifest);
        if (!missingAttachments.length) {
            return;
        }

        const missingUrls = missingAttachments.map((attachment) => attachment.url);
        const hasUnapprovedMissingAttachment = missingUrls.some((url) => !approvedMissingUrls.has(url));
        if (hasUnapprovedMissingAttachment) {
            this._syncLog.withMetadata({ 'archipad.sync.missing_temp_attachments': missingAttachments.length }).error('sync.publish.blocked_by_missing_temp_attachments');
            throw new MissingTempAttachmentsError(
                this._projectInfo.id,
                missingUrls,
                () => firstValueFrom(this._enqueuePublishChanges(null, new Set(missingUrls))),
            );
        }

        removeAttachmentsFromManifest(netPatch.attachmentManifest, missingAttachments);
        this._syncLog.withMetadata({ 'archipad.sync.missing_temp_attachments': missingAttachments.length }).warn('sync.publish.missing_temp_attachments_dropped');
    }

    /**
     * Extracts the local changes of the ORM as a patch in network format, ready to be published.
     */
    private *_exportPatchToPublish(progress: ProgressObserver, projectContext: CloudProjectContext): GeneratorType<NetPatch> {
        const p = makeProgressHandler(progress);
        p.total(2);

        const entityContext = projectContext.getEntityContext();
        const projectId = this._projectInfo.id;

        yield* callAsGenerator(p, 1, () => _PatchHandlerService.executePatchHandlers('willCreatePatch', entityContext, projectId, PatchHandlerContext.SYNCHRONIZE));
        // regular patch header
        const header = this._makePatchHeader(entityContext, projectContext.getClientName(), false);

        // get patch data
        const patch : MutablePatch = this.patchService.extractPatch(entityContext, header);

        // patch data without temp entities
        yield* call(p.oldTask(1, null, () => _PatchHandlerService.executePatchHandlers('willExportPatch', entityContext, projectId, PatchHandlerContext.SYNCHRONIZE, patch, synchroDriver.CURRENT_PATCH)));

        const netPatch = this.patchService.exportPatch(entityContext, this._attachmentSerializer, patch);
        if(!netPatch){
            throw new IllegalStateError('Publishing an empty patch: this error is due to a mismatch between the state of the temp patch and the actual exported patch');
        }
        return netPatch;
    }


    private *_getTempPatch(progress: ProgressObserver) : GeneratorType<synchroDriver.TempPatch> {
        const p = makeProgressHandler(progress);
        p.total(2);

        const tempPatch = yield* call(p.oldTask(1, null, () => this.cacheRepository.getTempPatch(this._userInfo.id, this._projectInfo.id)));

        const problem = validateTempPatch(tempPatch);
        if (problem === 'empty_patch_set') {
            throw new ReadEmptyPatchSetError();
        }
        if (problem === 'wrong_version') {
            this._setTempPatchStatus({
                state: synchroDriver.TempPatchState.CONFLICT,
                rebase: true,
            });
            const error = new ProjectError("Wrong temp patch version");
            error.state = this._getState();
            throw error;
        }

        this._updateTempPatchInfo(tempPatch, UpdateReason.Read);
        return tempPatch;
    }

    private *_getEmptyTempPatch() : GeneratorType<synchroDriver.TempPatch> {
        const availablePatches = yield* this._getAvailablePatches();
        const patch:synchroDriver.TempPatch = {
            patchSet: Array.from(availablePatches).sort(comparePatchId),
            patch: null,
            hasUnflushedChanges: false,
            synchroTime: null,
            version: TEMP_PATCH_VERSION,
        };
        return patch;
    }

    private *_persistTempPatch(tempPatch: synchroDriver.TempPatch): GeneratorType<synchroDriver.TempPatch> {
        log.warn("Persisting temp patch", tempPatch);
        this._updateApproxChangesCount(tempPatch);
    
        if (tempPatch.patchSet === undefined || tempPatch.synchroTime === undefined || tempPatch.patch === undefined || tempPatch.hasUnflushedChanges === undefined) {
            log.debug("incomplete temp patch", tempPatch);
            throw new IllegalStateError("Incomplete temp patch");
        }
        tempPatch.version = TEMP_PATCH_VERSION;
    
        if (tempPatch.patch && tempPatch.patch.patch.length < 2) {
            throw new IllegalStateError("got patch with no changes");
        }

        if (!tempPatch.patchSet?.length) {
            throw new WriteEmptyPatchSetError();
        }

        log.debug('saving local patch with synchroTime ' + tempPatch.synchroTime + " ref:" + tempPatch.patchSet.slice(-1).pop());
        yield* call(from(this.cacheRepository.setTempPatch(this._userInfo.id, this._projectInfo.id, tempPatch)));
        this._updateTempPatchInfo(tempPatch, UpdateReason.Write);
    
        return tempPatch;
    }

    /**
     * @param reason - Why the local changes are removed, `published` once they have been published
     */
    private *_removeTempPatch(reason: SyncReason): GeneratorType<void> {
        const tempPatchDescription = describeTempPatch(yield* this._readTempPatchForLog());

        yield* call(from(this.cacheRepository.removeTempPatch(this._userInfo.id, this._projectInfo.id)));
        this._updateTempPatchInfo(null, UpdateReason.Write);

        // removing local changes that have not been published is a data loss
        const attributes = { ...tempPatchDescription, 'archipad.sync.reason': reason };
        if (reason !== 'published' && tempPatchDescription['archipad.temp_patch.operations'] > 0) {
            this._syncLog.withMetadata(attributes).warn('sync.temp_patch.removed');
        } else {
            this._syncLog.withMetadata(attributes).info('sync.temp_patch.removed');
        }
        const previousSessionId = syncSessionService.startNewSession(this._projectInfo.id);
        this._syncLog.withMetadata({ 'archipad.sync.reason': reason, 'archipad.sync.previous_session_id': previousSessionId }).info('sync.session.start');
    }

    /**
     * Sync logger bound to this project.
     */
    private get _syncLog() {
        return syncLog.child().withContext({ 'archipad.project.id': this._projectInfo.id });
    }

    /**
     * Reads the temp patch for the sync logs only: unlike `_getTempPatch`, it does not check the
     * temp patch, does not update the sync state and never throws, so logging cannot change the sync behavior.
     */
    private *_readTempPatchForLog(): GeneratorType<synchroDriver.TempPatch | null> {
        try {
            return yield* call(from(this.cacheRepository.getTempPatch(this._userInfo.id, this._projectInfo.id)));
        } catch (error) {
            this._syncLog.withError(error).warn('sync.temp_patch.read_for_log_failed');
            return null;
        }
    }

    // Project status
    private *_updateHasUnfetchedChanges(progress: ProgressObserver): GeneratorType<boolean> {
        const p = makeProgressHandler(progress);
        p.total(4);
        
        // no new changes, project is not registered
        if (!this._projectInfo.registered){
            return false;
        }

        // project is on cloud
        const tempPatch = yield* this._getTempPatch(p.task(1));
        let hasMorePatches: boolean = false;
        if (!tempPatch) {
            // we don't have any temp patch, project never downloaded
            hasMorePatches = true;
        } else if (tempPatch && tempPatch.patchSet.length === 0) {
            // we are not based on any patch, new project
            hasMorePatches = false;
        } else {
            const patchesDir = new PatchesDescriptor(this.getId(), "patch"); 
            
            const patches = yield* call(synchroDriver.listProjectPatches(p.task(1), patchesDir));
            yield* callAsGenerator(p,1,(si, po) => this._discardOutdatedPatchesCached(si, po, patches));
            hasMorePatches = patches.findIndex(patch => (tempPatch.patchSet.indexOf(patch) === -1)) !== -1;
        }

        this._projectInfo.hasUnfetchedChanges = hasMorePatches;

        yield* call(p.oldTask(1, null, () => this.cacheRepository.setProjectInfo(this._userInfo.id, this._projectInfo.id, this._projectInfo)));
        
        return hasMorePatches;
    }



    private *_getContext(progress: ProgressObserver) : GeneratorType<CloudProjectContext> {
        const p = makeProgressHandler(progress);
        p.total(100);

        if(this.projectContext){
            return this.projectContext;
        }

        const aspects = yield* this._getAspects(p.task(2));

        const projectInfo = this._getProjectInfo()
        const participants = [];
        if (OfflineServiceInstance.isOnline()) {
            const projectParticipants = yield* call(sharedProjects.listParticipants(p.task(1), projectInfo.id));
            if (projectParticipants) {
                participants.push(...projectParticipants);
            }
        }


        const isOwner = projectInfo.isOwner;
        const isAdmin = participants.find(participant => participant.id === this._userInfo.id)?.role === "administrator"

        let configAspects = isOwner ? [ 'project_owner' ] : [];
        if (isAdmin) {
            configAspects.push('admin')
        }

        configAspects = configAspects.concat(aspects);

        const projectConfigBundle = findConfigBundleByName("project");
        projectConfigBundle.setEnabledAspects(configAspects);

        const shouldRebase = yield* this._isRebaseNeeded(p.task(2));
        this._syncLog.withMetadata({
            ...describeTempPatch(yield* this._readTempPatchForLog()),
            'archipad.project.should_rebase': shouldRebase,
        }).info('project.open');
        if (shouldRebase) {
            // call public API to have call notifications
            yield* this._rebase(p.task(88), false);
            log.debug("downloaded project " + this._projectInfo.name);
        } else {
            p.units(92);
        }

        // THIS is where the link between ProjectContext and ProjectManager starts...
        const projectContext : CloudProjectContext = new CloudProjectContextImpl(this);

        yield* projectContext.openProjectContext(p.task(2), OfflineServiceInstance.isOffline());
    
        const projectEntity = projectContext.getProjectEntity();

        yield* call(FormTemplate.driver.updateTemplateCacheForProject(null, projectEntity));
        yield* this._loadAspects(p.task(2), projectEntity);

        this.projectContext = projectContext;

        yield* this._synchronizeUsers(p.task(2), projectContext);

        projectContext.notifyContextReady();        
		return projectContext;
    }

    // Refreshes up to a latest version or temp patch's one
    /**
     * @param discardReason - Logged reason of the local changes removal when `discardTempChanges` is true
     */
    private *_rebase(progress: ProgressObserver, discardTempChanges?: boolean, exceptLastTempPatch?: boolean, discardReason: SyncReason = 'rebase'): GeneratorType<ProjectSynchroState> {
        const p = makeProgressHandler(progress);
        p.total(100);

        const projectId = this._projectInfo.id;
        this._syncLog.withMetadata({
            'archipad.sync.discard_temp_changes': Boolean(discardTempChanges),
            'archipad.sync.reason': discardTempChanges ? discardReason : undefined,
        }).info('sync.rebase.start');

        try {
            // Handle temp patch (clean it or get reference/status)
            if (discardTempChanges) {
                yield* this._removeTempPatch(discardReason);
            }

            // The local changes are kept: the project is rebuilt on the patches they are based on, only the missing ones are downloaded
            let tempPatch: synchroDriver.TempPatch | null = null;
            if (!discardTempChanges) {
                tempPatch = yield* this._getTempPatch(p.task(5));
            }

            const availablePatches = yield* this._getAvailablePatches();

            // Are we using an outdated dumpPatch in cache ?
            const isOutdatedDumpPatchInCache = yield* callAsGenerator( p, 30, ( si, po ) => this._isOutdatedDumpPatchInCache( si, po ) );
            if ( isOutdatedDumpPatchInCache ){
                log.warn('The project entity of this project is based on an erroneous dumpPatch. We will delete cached patches.');
                // remove All dumpPatches save on caches and delete them from availablePatches
                const dumpPatchesId = yield* callAsGenerator(p, 0, () => this.cacheRepository.removeDumpPatch(this._userInfo.id, projectId));
                for (const patchId of dumpPatchesId) {
                    delete availablePatches[patchId];
                }
                // Recursive call on itself to restart the rebase process after cleaning the corrupted patches
                return yield* this._rebase(progress,discardTempChanges,exceptLastTempPatch,discardReason);
            }

            // if the project is registered (on cloud, load patches)
            let hasMorePatchesToLoad = false;
            if (this._projectInfo.registered) {
                const manifests : {[key:string]:unknown} = {};

                const patchSet = tempPatch ? tempPatch.patchSet : null;
                const patchesDir = new PatchesDescriptor(this.getId(), "patch");
                // we have the reference and tempPatch, download missing patches
                const loadProjectPatchesCall = synchroDriver.loadProjectPatches(p.task(40), this.getId(), patchesDir, availablePatches, patchSet, (progress,patchesData) => {
                    // store the patch
                    return defer(async () => {
                        await this.cacheRepository.setPatchesData(this._userInfo.id, projectId, p.task(20),patchesData);
                        for (const patchData of patchesData){
                            manifests[patchData.patchId] = patchData.attachmentManifest;
                            availablePatches[patchData.patchId] = true;
                        }
                    })
                }, USE_DUMP_PATCH_MECHANISM);
                const ignoredPatches = yield* call(loadProjectPatchesCall);
                hasMorePatchesToLoad = !!ignoredPatches.length;
            }

            // save project info
            {
                // NOTE: seems useless now that we keep the patches on S3 after deleting projects 
                // if (!Object.keys(availablePatches).length && !tempPatch) {
                //     // no temp patch and no patches, project was removed from Cloud and we don't know it
                //     const error = new ProjectNotFound("Inexistant project " + projectId);
                //     throw error;
                // }

                this._projectInfo.hasUnfetchedChanges = hasMorePatchesToLoad;
                yield* call(p.oldTask(5, null, () => this.cacheRepository.setProjectInfo(this._userInfo.id, projectId, this._projectInfo)));
            }

            {
                let patch = tempPatch;
                if (patch) {
                    const kept = keepTempPatch(patch, yield* this._getAvailablePatches());
                    patch = kept.tempPatch;
                    if (kept.missingPatches.length > 0) {
                        this._syncLog
                            .withMetadata({ ...describeTempPatch(patch), 'archipad.temp_patch.missing_patches': kept.missingPatches.length })
                            .warn('sync.temp_patch.rebased');
                    }
                } else {
                    patch = yield* this._getEmptyTempPatch();
                }
                yield* this._persistTempPatch(patch);
                this._setStatusSynced();
            }

            // reload orm
            if(this.projectContext){
                yield* call(this.projectContext.reload());
            }
            return this._getState();
        }
        catch(e) {
            if (e instanceof CancelledError) {
                throw e;
            }

            log.error("Unable to rebase", e, e.stack);

            const apiError = findInAllChainedErrors(e, ResponseAPIError.is);
            if (apiError?.response.code === 41) {
                throw chainError(e, new ProjectNotFound());
            }

            throw e;
        }            
    }

    private async _isOutdatedDumpPatchInCache(signal: AbortSignal, progress: ProgressObserver): Promise<boolean> {
        const p = makeProgressHandler(progress);
        p.total(100);
        const linkedEmptyPatches = await callAsAsync( signal, p, 25, ( _po ) => this.cacheRepository.getPatchesIdByNature( this._userInfo.id, this._projectInfo.id, PatchNature.LinkedEmptyPatch ) );
        if (!linkedEmptyPatches.size){
            return false;
        }
        const dumpPatchIDs = await callAsAsync( signal, p, 25, ( _po ) => this.cacheRepository.getPatchesIdByNature( this._userInfo.id, this._projectInfo.id, PatchNature.DumpPatch ) );
        const dumpPatchID = [...dumpPatchIDs][0];
        
        const patchesDir = new PatchesDescriptor(this.getId(), "patch");

        const allPatches = await callAsAsync( signal, p, 45, (po) => synchroDriver.listProjectPatches( po, patchesDir ));
        
        await callAsAsync(signal, p, 5,(po) => this._discardOutdatedPatchesCached(signal, po, allPatches));

        const lastPatchInPatchSet = [...linkedEmptyPatches].pop();

        const checkThereIsInterlacedPatch = Boolean(allPatches.filter(value => {
            const isNotTheDumPatch = value !== dumpPatchID;
            const isNotIncludeInPatchSet = linkedEmptyPatches.has(value) === false;
            const isInterlacedPatch = comparePatchId(value,lastPatchInPatchSet) === -1;
            return isNotTheDumPatch && isNotIncludeInPatchSet && isInterlacedPatch;
        }).length);
        const checkThereIsNotExcessPatchInPatchSet = Boolean(Array.from(linkedEmptyPatches).filter((localPatchId) => allPatches.includes(localPatchId) === false).length);

        return checkThereIsInterlacedPatch || checkThereIsNotExcessPatchInPatchSet;
    }

    /**
     * Publishes current changes
     *
     * @param approvedMissingUrls - Missing temp attachments the user agreed to publish without, see `_ensureTempAttachmentsArePublishable`
     */
    private *_publishChanges(progress: ProgressObserver, ctx?: CloudProjectContext, approvedMissingUrls: ReadonlySet<string> = new Set()): GeneratorType<ProjectSynchroState> {
        const p = makeProgressHandler(progress);
        p.total(100);

        this._syncLog.withMetadata({
            'archipad.sync.has_unpublished_changes': this._hasUnpublishedChanges,
            'archipad.sync.has_context': Boolean(ctx),
        }).info('sync.publish.start');

        let projectContext : CloudProjectContext = ctx;
        if(!ctx){
            // WORKAROUND: During a project creation (in the middle of),
            // this method was called without context while the project was not in consistent state.
            // The call must be silently ignored.
            if (!this._hasUnpublishedChanges){
                log.error("project manager is not properly initialized. 'publishChanges' call aborted and ignored");
                this._syncLog.withMetadata({ 'archipad.sync.skip_reason': 'no_context_and_no_unpublished_changes' }).info('sync.publish.skipped');
                return this._getState();
            }

            projectContext = yield* this._getContext(p.task(20));
        }

        // flush ORM
        yield* call(projectContext.flushORMChanges());       

        if (!this._hasUnpublishedChanges){
            this._syncLog.withMetadata({ 'archipad.sync.skip_reason': 'no_unpublished_changes' }).info('sync.publish.skipped');
            return this._getState();
        }

        // if we're not in sync, don't even try
        const currentState = this._getState();
        if ( ! (!currentState.tempPatchStatus ||
            currentState.tempPatchStatus.state == synchroDriver.TempPatchState.IN_SYNC ||
            currentState.tempPatchStatus.state == synchroDriver.TempPatchState.PUSH) )
        {
            this._syncLog.withMetadata({ 'archipad.sync.state': currentState.tempPatchStatus.state }).warn('sync.publish.invalid_state');
            const error = new ProjectError("Trying to push but not correct state");
            error.state = currentState;
            throw error;
        }

        // if not yet registered, call backend
        if (!this._projectInfo.registered) {
            yield* call(p.oldTask(5, null, (task) => synchroDriver.registerProject(task, this._projectInfo)));
            this._projectInfo.registered = true;
            yield* call(p.oldTask(0, null, (task) => this.cacheRepository.setProjectInfo(this._userInfo.id, this._projectInfo.id, this._projectInfo)));
        } else {
            p.units(5);
        }

        const status = this._getState().tempPatchStatus;

        const currentTempPatch = yield* this._getTempPatch(p.task(0));

        // if we're not in sync, stop
        if ( ! (status.state == synchroDriver.TempPatchState.IN_SYNC ||
                status.state == synchroDriver.TempPatchState.PUSH) )
        {
            log.warn("Trying to push but not correct state");
            this._syncLog.withMetadata({ 'archipad.sync.state': status.state }).warn('sync.publish.invalid_state');

            this._setTempPatchStatus(status);

            const error = new ProjectError("Trying to push but not correct state");
            error.state = this._getState();
            throw error;
        }

        // all good
        log.info('saving project ' + this._projectInfo.id);

        const projectInfo = this._projectInfo;
        if(!projectInfo.id || !projectInfo.name){
            throw new IllegalArgumentError(`Invalid project info`);
        }

        const netPatch = yield* this._exportPatchToPublish(p.task(0), projectContext);
        yield* this._ensureTempAttachmentsArePublishable(p.task(0), netPatch, approvedMissingUrls);

        // Until the upload below succeeds, a failure keeps the local changes: the next publish retries them.
        this._syncLog.withMetadata(describeTempPatch(currentTempPatch)).info('sync.publish.save');
        let savedPatch: synchroDriver.SavedPatch;
        try {
            // No dependencies: publishing changes never changes them (see _setDependencies()), and the local ones
            // may be stale - the server replaces its own with whatever it's sent.
            savedPatch = yield* call(synchroDriver.saveProjectPatch(p.task(65), { id:projectInfo.id, name:projectInfo.name }, this._attachmentSerializer, netPatch));
        } catch (error) {
            this._syncLog.withError(error).error('sync.publish.failed');
            throw error;
        }
        this._syncLog.withMetadata({ 'archipad.patch.id': savedPatch.id }).info('sync.publish.done');

        // The patch is published: clear the local changes right away, before anything else can fail.
        const state = yield* this._rebase(p.task(5), true, undefined, 'published');

        // Only now delete the temp attachments of the patch, which the local changes referenced until the rebase.
        // Best effort: a failure is logged and leaves orphan files in the temp folder, the local changes are safe.
        yield* call(synchroDriver.cleanUpTempAttachments(p.task(5), projectInfo.id, savedPatch.tempAttachments));

        log.info('project ' + this._projectInfo.id + ' published');

        // Trigger background preload of attachments
        this.triggerPreLoadProjectAttachments.next();

        return state;
    }

    private *_isRebaseNeeded(progress: ProgressObserver): GeneratorType<boolean> {
        const p = makeProgressHandler(progress);
        p.total(1);

        const tempPatch = yield* this._getTempPatch(p.task(1));
        
        // only rebase if no temp patch or not a new project
        // 1. no temp patch: reference: null :
        //      never touched the project : rebase
        //      hasPatches : no need to rebase
        // 2. initial reference : new project, no need to rebase
        // 3. other reference : need to check we're at correct revision
        let shouldRebase = true;
        if (tempPatch) {
            if (tempPatch.patchSet.length === 0){
                shouldRebase = false; // new project
            }
            else if (yield * this._hasAllPatches(tempPatch.patchSet)){
                shouldRebase = false; // we have all patches, no need to rebase
            }
        }

        return shouldRebase;
    }

    private *_getAspects(progress: ProgressObserver): GeneratorType<string[]> {
        const p = makeProgressHandler(progress);
        p.total(1);

        return yield* call(p.oldTask(1, null, (task) => dependenciesManager.getAspectsFromDependency(task, this._projectInfo.dependencies, this._projectInfo.id)));
    }

    private *_loadAspects(progress:ProgressObserver, project: ProjectEntity): GeneratorType<void> {
        const p = makeProgressHandler(progress);
        p.total(2);

        const accountService = getAccountService();
        const userInfo = accountService.currentUser;

        const getAspectsCall = forkJoin([
            p.oldTask(1, null, (task) => dependenciesManager.getProjectAspects(task, this._projectInfo.dependencies, this._projectInfo.id)),
            p.oldTask(1, null, (task) => dependenciesManager.getParticipantAspects(task, project, userInfo)),
        ]);
        const [ projectAspects, participantAspects ] = yield* call(getAspectsCall);

        const projectParticipantConfigBundle = findConfigBundleByName("project.participant");
        projectParticipantConfigBundle.setEnabledAspects(participantAspects);

        this._projectInfo.aspects = [].concat(projectAspects, participantAspects);
    }

    private *_synchronizeUsers(progress:ProgressObserver, projectContext:ProjectContext):GeneratorType<void> {
        const p = makeProgressHandler(progress);
        p.total(1);
        
        if (!isBugWorkflowEnabled(projectContext.getProjectEntity())) {
            p.units(1);
            return;
        }
        if (OfflineServiceInstance.isOnline()) {
            sharedProjects.clearParticipantsCacheForProject(this._projectInfo.id);
        }
        

        const participants = yield* call(sharedProjects.listParticipants(p.task(1), this._projectInfo.id));

        if (!participants) {
            // no participants available (e.g. offline cache miss or no current user): skip to avoid crashing the upgrade
            return;
        }

        const users = participants.map((participant) => {
            const x : UserDataJson = {
                cloudUserId: participant.id,
                email: participant.email,
                primaryRole: participant.role,
                roleData: participant.roleData,
                isOwner: participant.isOwner,
                name: participant.name,
                company: participant.company,
            };
            return x;
        });

        yield * call(from(workflowUserManager.synchronizeUsers(projectContext.getProjectEntity(), users)));
    }

    private *_doUpgrade(progress: ProgressObserver, projectContext: ProjectContext): GeneratorType<boolean> {
        const p = makeProgressHandler(progress);
        p.total(100);

        // update operation: create upgrade patches directly on server
        const upgrade = yield* call(projectTemplateManager.applyTemplateUpgrade(p.task(50), projectContext.getProjectEntity(), onSavePatch));

        if ( upgrade.applied ) {
            // fetch latests patches - created by upgrade
            yield* this._rebase(p.task(35), true, undefined, 'template_upgrade');

            // Only now delete the temp attachments of the upgrade patches, which the local changes referenced until the rebase.
            yield* call(synchroDriver.cleanUpTempAttachments(p.task(5), this._projectInfo.id, upgrade.tempAttachments));

            bugNotePhase.upgradeBugNotes(projectContext.getProjectEntity());
            
            yield* this._synchronizeUsers(p.task(10), projectContext);
            
            return true;
        } else {
            p.units(50);
            return false;   // no need to refresh dependencies
        }
    }

    /**
     * Publishes the local changes the nominal way, ahead of a template upgrade: the upgrade publishes its own patches
     * straight to the server and commits the ORM context, which would sweep the changes still local into them - or
     * drop them. Returns false when they couldn't be published (remote changes to fetch first, network...): the
     * upgrade then waits for a later opening, the local changes being kept as they are.
     */
    private *_publishLocalChangesBeforeUpgrade(progress: ProgressObserver, context: CloudProjectContext): GeneratorType<boolean> {
        yield* call(context.flushORMChanges());
        if (!this._hasUnpublishedChanges) {
            return true;
        }

        try {
            yield* this._publishChanges(progress, context);
        } catch (error) {
            log.warn('Template upgrade postponed: the local changes could not be published first', error);
            return false;
        }

        // The rebase that follows a publish only reloads the context once the project is open, not while it opens.
        if (this.projectContext !== context) {
            yield* call(context.reload());
        }
        return !this._hasUnpublishedChanges;
    }

    private *_upgradeProjectUsingContext(progress: ProgressObserver, context: CloudProjectContext):GeneratorType<boolean> {
        const p = makeProgressHandler(progress);
        p.total(100);

        if (!(yield* this._publishLocalChangesBeforeUpgrade(p.task(1), context))) {
            return false;
        }

        const ret = yield* this._doUpgrade(p.task(75), context);

        if(!ret){
            return false;
        }

        // make sure operation changes are pushed
        yield* this._publishChanges(p.task(22), context);
        yield* call(context.reload());
        
        const projectEntity = context.getProjectEntity();

        // upgrade dependencies
        this._upgradeProjectDependencies(projectEntity);

        // update dependencies - unless the model has none: the known ones are kept (never replaced by none)
        const dependencies = projectEntity.dependencies ? projectEntity.dependencies.split(',').filter((dependency) => dependency !== '') : [];
        if (dependencies.length) {
            this._projectInfo.dependencies = dependencies;
        }
        this._projectInfo.hasWorkflow = this._projectInfo.dependencies.indexOf('workflow') !== -1;

        yield* call(p.oldTask(1, null, (task) => this.cacheRepository.setProjectInfo(this._userInfo.id, this._projectInfo.id, this._projectInfo)));
        yield* this._setDependencies(p.task(1), this._projectInfo.dependencies);

        // make sure operation changes are pushed
        yield* this._publishChanges(p.task(1), context);

        return true;

    }

    private _upgradeProjectDependencies(projectEntity: ProjectEntity): void {
        if (!projectEntity) {
            return;
        }

        // update formAspects cache if needed
        // WARNING : formAspects field used to store the project dependencies and NOT the project aspects
        // WE MUST HAVE A 1:1 mapping between project dependencies and project aspects : a project dependency can ONLY have ONE project aspect with the SAME name.

        // - if projectEntity.dependencies is set, it always wins
        // - if dataManager.getDependencies() is an array then this is second best guess, comes from server
        // - if projectEntity.formAspects is a string then third best guess, historical

        if ( !projectEntity.dependencies ) {
            // model doesn't have dependencies.

            if ( this.getDependencies().length ) {
                // server has dependencies, update model from server
                projectEntity.dependencies = this.getDependencies().filter(x => !x.endsWith(dependenciesManager.REPORT_TEMPLATE_DEPENDENCY_MARKER)).join(',');
            }    

            if( !projectEntity.dependencies && projectEntity.formAspects) {
                // project entity has form aspects, update dependencies using formAspects
                projectEntity.dependencies = projectEntity.formAspects;
            }
        }
    }

    private *_update(progress:ProgressObserver, remoteInfo: CloudProjectInfo, lastPatchId:string|null): GeneratorType<boolean> {
        const p = makeProgressHandler(progress);
        p.total(1);

        this._projectInfo.registered = true;

        // TODO Add logic to compare `photoUrl`.
        // Since it is a sign url it always changes, which defeats browser image caching.
        // TODO Use the expiry parameter in the url, and add a policy to update `photoUrl` or not (example: use half life).
        let modified = false;
        const merge = (key) => {
            if (this._projectInfo[key] != remoteInfo[key] && remoteInfo[key] !== undefined) {
                this._projectInfo[key] = remoteInfo[key];
                modified = true;
            }
        }

        const keys : Array<keyof CloudExtendedProjectInfo> = ['created', 'lastModified', 'isOwner', 'photoUrl', 'hasWorkflow', 'isSampleProject', 'offlineFlag'];
        keys.map(merge);

        // The server's dependencies are the reference: a stale local copy (e.g. saved before they changed
        // server-side, or partially by a template) must not be kept, let alone pushed back.
        const remoteDependencies = remoteInfo.dependencies;
        if (remoteDependencies !== undefined && remoteDependencies.join(',') !== this._projectInfo.dependencies.join(',')) {
            this._projectInfo.dependencies = [ ...remoteDependencies ];
            modified = true;
        }

        if (!this._hasUnpublishedChanges) {
            merge('name');
        }

        if(lastPatchId) {
            const availablePatches = yield* this._getAvailablePatches();
            if (availablePatches.size > 0) {
                const lastAvailablePatchId = Array.from(availablePatches).sort(comparePatchId).pop();
                this._projectInfo.hasUnfetchedChanges = (lastAvailablePatchId !== lastPatchId);
            }
        }

        yield* call(p.oldTask(1, null, (task) => this.cacheRepository.setProjectInfo(this._userInfo.id, this._projectInfo.id, this._projectInfo)));

        return modified;
    }

    private *_remove(progress:ProgressObserver): GeneratorType<void> {
        const p = makeProgressHandler(progress);
        p.total(100);

        yield* call(p.oldTask(10, null, (task) => this.cacheRepository.removeProject(task, this._userInfo.id, this._projectInfo.id)));

        yield* call(p.oldTask(80, null, (task) => synchroDriver.deleteProjects(task, [this._projectInfo.id])));
    }

    private *_updateChangeCount(progress: ProgressObserver): GeneratorType<void> {
        const p = makeProgressHandler(progress);
        p.total(1);

        const tempPatch = yield* this._getTempPatch(p.task(1));
        
        // init changes count
        this._updateApproxChangesCount(tempPatch);
    }

    private *_setDependencies(progress: ProgressObserver, deps: string[]): GeneratorType<void> {
        const p = makeProgressHandler(progress);
        p.total(1);

        this._projectInfo.dependencies = deps;
        const projectInfo = this._projectInfo;
        yield* callAsGenerator(p, 1, (signal, po) => synchroBackend.updateProject(signal, po, projectInfo, null));
    }

    private *_closeContext(progress: ProgressObserver): GeneratorType<void> {
        const projectContext = this.projectContext;
        if(projectContext) {    // make sure the context is not already closed or did not had the time to open
            yield* call(projectContext.close());
            this.projectContext = null;
        }

        const projectConfigBundle = findConfigBundleByName("project");
        const projectParticipantConfigBundle = findConfigBundleByName("project.participant");

        // reset the config aspects
        projectConfigBundle.setEnabledAspects([]);
        projectParticipantConfigBundle.setEnabledAspects([]);
    }


    //---------------------------------------------------------------------------
    // ProjectContextDataManager implementation

    public getAttachmentSerializer(): AttachmentSerializer {
        return this._attachmentSerializer;
    }

    private getDependencies() : string[] { return this._projectInfo.dependencies; }

    public getPatches(): Observable<string[]>{
        return task(() => this._getAvailablePatches()).pipe(
            map(function(availablePatches) { return Array.from(availablePatches).sort(comparePatchId); }),
        );
    }

    public makePatchHeader(entityContext:EntityContext, clientName:string, temp:boolean) : MutablePatchHeader {
        return this._makePatchHeader(entityContext, clientName, temp);
    }

    public getPatchData(patchId:string): Observable<{patch: NetPatch, nature: PatchNature}> {
        return task(() => this._getPatch(null, patchId));
    }

    private getTempPatchData(): Observable<NetPatch> {
        return task(() => this._getTempPatch(null)).pipe(
            map((tempPatch) => {
                return tempPatch ? tempPatch.patch : null;
            }),
        )
    }

    public *applyPatchesDidCommit(referenceContext:EntityContext): GeneratorType<void> {
        // apply temp patch
        const changeData = yield* call(this.getTempPatchData());
        if (changeData) {
            const header : NetPatchHeader = changeData.patch[0];
            if (!header.tempData) {
                header.tempData = { emptyLike:false };
            }
            const patch = this.patchService.importPatch(referenceContext, this.getAttachmentSerializer(), changeData.patch, changeData.attachmentManifest);
            this.patchService.applyPatch(referenceContext, patch);
        }
    }

    private *_onContextModified(projectName:string, patch: PatchData, onUserInput:boolean) : GeneratorType<void> {
        const p = makeProgressHandler(null);
        
        let oldTempPatch = yield* this._getTempPatch(null);
        if (!oldTempPatch) {
            oldTempPatch = yield* this._getEmptyTempPatch();
        }

        const newTempPatch : synchroDriver.TempPatch = {
            patchSet: oldTempPatch.patchSet,
            patch: patch,
            synchroTime: oldTempPatch.synchroTime,
            hasUnflushedChanges: onUserInput,
            version: 0,
        };
        yield* this._persistTempPatch(newTempPatch);

        if (projectName != this._projectInfo.name) {
            this._projectInfo.name = projectName;

            yield* call(p.oldTask(0, null, () => this.cacheRepository.setProjectInfo(this._userInfo.id, this._projectInfo.id, this._projectInfo)));
        }

        this._contextModified();
    }

    /**
     * Checks that the patchesData stored locally are also present remotely. If not, restores the integrity of the project.
     * @param remotePatches List of patches store remotely (Like S3) 
     */
    private async _discardOutdatedPatchesCached(signal: AbortSignal, progress:ProgressObserver, remotePatches: string[]) : Promise<void> {
        const p = makeProgressHandler(progress);
        p.total(4);

        const localPatchesSet = await callAsAsync(signal, p, 1, (_po) => this.getPatches());
        const localPatches = Array.from(localPatchesSet);

        const localPatchesOutdated = localPatches.filter(patch => !remotePatches.includes(patch));
        if (localPatchesOutdated.length > 0) {
            log.warn("Discarding outdated patches", localPatchesOutdated);
            const outdatedPatchesData = await Promise.all(localPatchesOutdated.map(
                async patch => {
                    return {
                        patchId: patch,
                        ...(await this.cacheRepository.getPatchData(this._userInfo.id, this._projectInfo.id, patch)),
                    }
                }));
            for (const outdatedPatch of outdatedPatchesData.filter(p => p.nature === PatchNature.PatchData)) {
                await callAsAsync(signal, p, 0, (_po) => this.cacheRepository.removePatchData(this._userInfo.id, this._projectInfo.id, outdatedPatch.patchId));
            }
            if (outdatedPatchesData.some(p => p.nature === PatchNature.DumpPatch || p.nature === PatchNature.LinkedEmptyPatch)) {
                await callAsAsync(signal, p, 0, (_po) => this.cacheRepository.removeDumpPatch(this._userInfo.id, this._projectInfo.id));
            }
            const tempPatch = await callAsAsync(signal, p, 1, (po) => task(() => this._getTempPatch(po)));
            if (tempPatch && tempPatch.patchSet){
                tempPatch.patchSet = tempPatch.patchSet.filter(patch => localPatchesOutdated.indexOf(patch) === -1);
                await callAsAsync(signal, p, 1, (_po) => task(() => this._persistTempPatch(tempPatch)));
            }
            await callAsAsync(signal, p, 2, (po) => task(() => this._rebase(po, false)));
        }
    }

    public contextModified(projectName:string, patch: PatchData, onUserInput:boolean): Observable<void> {
        return task(() => this._onContextModified(projectName, patch, onUserInput));
    }

    public upgradeProjectUsingContext(progress: ProgressObserver, context: ProjectContext): Observable<boolean> {
        return task(() => this._upgradeProjectUsingContext(progress, context as CloudProjectContext));
    }


    //---------------------------------------------------------------------------

    public refreshProjectInfo(progress: ProgressObserver): Observable<void> {
        const signal = voidAbortSignal();
        const po = makeProgressHandler(progress);
        po.total(2);
        const updateChangeCount$ = from(callAsAsync(signal, po, 1, (po) => task(() => this._updateChangeCount(po))));
        const isOfflineMode = this._projectInfo.offlineFlag;
        let preFetchProject$: Observable<unknown> = of(null);
        if (isOfflineMode){
            console.debug("Offline mode, pre-fetching project data", this._projectInfo);
            // Fetch listParticipants
            const fetchListParticipants$ = from(callAsAsync(signal, po, 1, (po) => sharedProjects.listParticipants(po, this._projectInfo.id)));
            // Fetch local patches

            const fetchProjectPatches$ = from(callAsAsync(signal, po, 1, (po) => task(() => this._getAvailablePatches()))).pipe(
                switchMap((availablePatches) => {
                    if (availablePatches.size) {
                        return of(null);
                    }
                    return from(callAsAsync(signal, po, 1, (po) =>task(() => this._rebase(po))));
                }), 
            );

            const fetchAspects$ = from(callAsAsync(signal, po, 1, (po) => task(() => this._getAspects(po))));
            
            const projectInteraction$ = from(callAsAsyncFromTask(signal, po, 1, (task) => getProjectInteraction(task, this._projectInfo.id)));
            preFetchProject$ = forkJoin([fetchListParticipants$, fetchProjectPatches$, fetchAspects$, projectInteraction$]).pipe(
                switchMap(() => this._requestPreloadProjectAttachments()),
            );
        }
        return updateChangeCount$.pipe(
            switchMap(() => preFetchProject$),
            map(() => {}),
        );

    }



    public getInfo(): Readonly<CloudExtendedProjectInfo> {
        return this._getProjectInfo();
    }

    /* Changes stuff */

    // publish temp changes as a real patch
    public publishChanges(progress: ProgressObserver): Observable<unknown> {
        return this._enqueuePublishChanges(progress, new Set());
    }

    /**
     * @param approvedMissingUrls - Missing temp attachments the user agreed to publish without (a distinct task key so
     * that this publish is not merged with a pending regular one, which would fail again on these attachments)
     */
    private _enqueuePublishChanges(progress: ProgressObserver, approvedMissingUrls: ReadonlySet<string>): Observable<ProjectSynchroState> {
        return taskQueue.addTask<ProjectSynchroState, TaskContext>(progress, {
            key: approvedMissingUrls.size ? 'publishChangesWithoutMissingAttachments' : 'publishChanges',
            context: { projectId: this.getId() },
            taskFn: (progress) => {
                return task(() => this._publishChanges(progress, undefined, approvedMissingUrls))
            },
        });
    }

    // discards local changes and get latest version
    public rebase(progress: ProgressObserver): Observable<unknown> {
        return taskQueue.addTask<unknown, TaskContext>(progress, {
            key: 'rebase', 
            context: { projectId: this.getId() },
            taskFn: (progress) => {
                return task(() => this._rebase(progress, true, false))
            },
        });
    }

    public hasContext(): boolean {
        return Boolean(this.projectContext);
    }

    // updates local changes by using remote changes
    public fetchLatestTempChanges(progress: ProgressObserver): Observable<ProjectSynchroState> {
        return taskQueue.addTask<ProjectSynchroState, TaskContext>(progress, {
            key: 'fetchLatestTempChanges', 
            context: { projectId: this.getId() },
            taskFn: (progress) => {
                return task(() => this._rebase(progress));
            },
        });
    }

    // resolve conflict using cloud changes
    public discardTempLocalChanges(progress: ProgressObserver): Observable<ProjectSynchroState>  {
        return taskQueue.addTask<ProjectSynchroState, TaskContext>(progress, {
            key: 'discardTempLocalChanges', 
            context: { projectId: this.getId() },
            taskFn: (progress) => {
                return task(() => this._rebase(progress, true, true, 'discard_temp_local_changes'));
            },
        });
    }

    public logTempChanges(): Observable<void> {
        return task(() => this._getTempPatch(null)).pipe(
            map((tempPatch) => {
                if (tempPatch.patch) {
                    const header: NetPatchHeader = tempPatch.patch.patch[0];
                    if (header.tempData && header.tempData.emptyLike) {
                        log.info("No local changes, only temporary data", tempPatch.patch);
                    } else {
                        log.info("Local changes", tempPatch.patch);
                    }
                } else {
                    log.info("No local changes");
                }
            }),
        );
    }

    public tempPatchIsEmptyLike(): Observable<boolean> {
        return task(() => this._getTempPatch(null)).pipe(
            map((tempPatch) => {
                if (tempPatch && tempPatch.patch) {
                    const header : NetPatchHeader = tempPatch.patch.patch[0];
                    if (header.tempData && header.tempData.emptyLike) {
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return true;
                }
            }),
        )
    }

    public refreshSynchroState(progress: ProgressObserver): Observable<ProjectSynchroState> {
        return task(() => this._refresh(progress));
    }

    public clear(progress: ProgressObserver): Observable<void> {
        return taskQueue.addTask<void, TaskContext>(progress, {
            key: 'clear', 
            context: { projectId: this.getId() },
            taskFn: (progress) => {
                const p = makeProgressHandler(progress);
                p.total(1);
                return p.oldTask(1, null, (task) => this.cacheRepository.removeProject(task, this._userInfo.id, this._projectInfo.id));
            },
        });
    }

    /* Context creation */

    public getContext(progress: ProgressObserver): Observable<CloudProjectContext> {
        return this._resilientGetContext(progress);
    }

    /**
     * We cannot open a new context if the old one is still being closed.
     * This method therefore includes a retry process to "wait" for a potential context closure.
     */
    private _resilientGetContext(progress: ProgressObserver, retryCount: number = 0): Observable<CloudProjectContext> {
        const currentProject = getCurrentProject();
        if (currentProject && currentProject.id !== this.getId()) {
            if ( retryCount > CONST_MAX_ATTEMPTS_GET_CONTEXT ) {
                throw new Error('Illegal attempt to open the context before closing the existing one');
            }
            return timer(CONST_WAIT_FOR_GET_CONTEXT_BETWEEN_ATTEMPTS).pipe(
                switchMap(() => this._resilientGetContext(progress, retryCount + 1)),
            );
        }
        return taskQueue.addTask<CloudProjectContext, TaskContext>(progress, {
            key: 'getContext',
            context: { projectId: this.getId() },
            taskFn: (progress) => task(() => this._getContext(progress)),
        });
    }

    public closeContext(progress: ProgressObserver): Observable<void> {
        // close context
        return taskQueue.addTask<void, TaskContext>(progress, {
            key: 'closeContext', 
            context: { projectId: this.getId() },
            taskFn: (progress) => task(() => this._closeContext(progress)),
        });
    }

    public update(progress:ProgressObserver, remoteInfo: CloudProjectInfo, lastPatchId:string|null): Observable<boolean> {
        return task(() => this._update(progress, remoteInfo, lastPatchId));
    }

    public remove(progress:ProgressObserver): Observable<void> {
        // close context
        return taskQueue.addTask<void, TaskContext>(progress, {
            key: 'remove', 
            context: { projectId: this.getId() },
            taskFn: (progress) => {
                return task(() => this._remove(progress));
            },
        });
    }

    public saveProjectAsTemplate(progress: ProgressObserver, name:string):Observable<string> {
        return task(() => this._saveProjectAsTemplate(progress, name));
    }

    private * _saveProjectAsTemplate(progress: ProgressObserver, name:string):GeneratorType<string> {
        const p = makeProgressHandler(progress);
        p.task(100);
    
        const projectContext = yield* call(this.getContext(p.task(5)));
        const projectId = this.getId();

        yield* this._upgradeProjectUsingContext(p.task(5), projectContext as CloudProjectContext);

        const result = yield* projectTemplateManager.saveProjectAsTemplate(p.task(90), name, projectContext, projectId);
        return result;
    }
    
    public synchronizeUsers(progress: ProgressObserver): Observable<void> {
        return taskQueue.addTask<void, TaskContext>(progress, {
            key: 'synchronizeUsers', 
            context: { projectId: this.getId() },
            taskFn: (progress) => {
                const p = makeProgressHandler(progress);
                p.total(100);

                const getContext$ = task(() => this._getContext(p.task(90)));
                return getContext$.pipe(
                    switchMap((projectContext) => {
                        return task(() => this._synchronizeUsers(progress, projectContext))
                    }),
                );
            },
        });
    }

    public addBlockingTask<T = unknown>(progress: ProgressObserver, key: string, taskFn: (progress: ProgressObserver) => Observable<T>): Observable<T>{
        this.projectContext.isFlushEnabled(false)
        
        const task = taskQueue.addTask(progress, {
            key: key, 
            context: { projectId: this.getId(), blocking:true },
            taskFn: (progress) => {
                return taskFn(progress);
            },
        })

        return task.pipe(tap(()=> this.projectContext.isFlushEnabled(true)));
    }
}


export function MakeProjectManager(userInfo: PatchUserInfo, projectInfo: CloudProjectInfo | LocalProjectInfo): ProjectManager {
    return new ProjectManagerImpl(userInfo, projectInfo);
}
